import type { DetailedHelpMap } from "./types";
import { estadoDaDataDeNegocio } from "./helpFacts";

/**
 * Ajuda detalhada: Visão geral, Ocupação, Reservas (+ modais), Hóspedes,
 * Portal dos hóspedes. Conteúdo verificado contra o código real — nunca
 * invente um campo/regra sem confirmar no componente/controller correspondente.
 */
export const OPERACAO_HOSPEDES_HELP: DetailedHelpMap = {
  "/visao-geral": [
    {
      title: "O que esta tela faz",
      description:
        "É o resumo do dia: o que precisa acontecer hoje (chegadas e saídas) e como o hotel está indo (ocupação e faturamento). É a primeira tela a olhar de manhã.",
      items: [
        {
          label: "O seu dia hoje",
          explanation:
            "Chegadas são reservas confirmadas que devem dar entrada hoje; saídas são hóspedes hospedados com data de saída hoje. Os dois viram a fila de trabalho da recepção.",
          fact: (ctx) => {
            const { bookings_arriving_today: chegando, bookings_departing_today: saindo, bookings_checked_in: dentro } = ctx.screen;
            if (chegando === undefined && saindo === undefined) return null;
            return `${chegando ?? 0} chegada(s) e ${saindo ?? 0} saída(s) previstas para hoje; ${dentro ?? 0} hóspede(s) no hotel agora.`;
          },
        },
        {
          label: "Data de negócio",
          explanation:
            "É em que dia o hotel está para o sistema. Ela não vira sozinha à meia-noite: só avança quando a auditoria noturna roda. Por isso, se ela estiver atrasada, os números desta tela também estão.",
          fact: (ctx) => estadoDaDataDeNegocio(ctx.screen),
        },
      ],
    },
    {
      title: 'Selo "Sistema operacional"',
      description: "Indicador estático no topo da página — não é uma verificação em tempo real de infraestrutura, é apenas o rótulo padrão da tela.",
    },
    {
      title: "Cards de KPI (Ocupação, Diária Média, Check-ins, RevPAR)",
      description: "Cada card mostra o número do dia e a variação. Os valores vêm agregados do backend a partir das reservas e diárias já lançadas — não são estimativas manuais.",
      items: [
        { label: "Ocupação", explanation: "Percentual de quartos vendidos hoje sobre o total de quartos ativos do hotel." },
        { label: "Diária Média (ADR)", explanation: "Valor médio das diárias vendidas no período — soma do valor das diárias dividido pela quantidade." },
        { label: "Check-ins Hoje", explanation: "Quantidade de reservas com chegada prevista para hoje; o texto abaixo mostra os check-outs previstos no mesmo dia." },
        { label: "RevPAR", explanation: "Receita por quarto disponível (Revenue per Available Room) — receita total dividida pelo total de quartos, inclusive os vazios." },
      ],
    },
  ],
  "/ocupacao": [
    {
      title: "O que esta tela faz",
      description:
        "É a visão de quem está onde e quando. Cada linha é um quarto físico, cada coluna é um dia, e cada bloco colorido é uma reserva ocupando aquele quarto naqueles dias. A cor indica de onde a reserva veio (direta, OTA, empresa) — a legenda fica acima da grade. Buraco na linha é quarto livre para vender.",
    },
    {
      title: "Mapa (grade de quartos x dias)",
      items: [
        {
          label: "O que a grade está mostrando agora",
          explanation:
            "A quantidade de linhas é a quantidade de quartos físicos cadastrados. Quarto fora de operação continua aparecendo na grade, mas com a faixa de manutenção — ele existe, só não pode ser vendido.",
          fact: (ctx) => {
            const { rooms_total: total, rooms_out_of_order: fora } = ctx.screen;
            if (total === undefined) return null;
            return fora
              ? `${total} quartos no mapa, sendo ${fora} fora de operação.`
              : `${total} quartos no mapa, nenhum fora de operação.`;
          },
        },
        {
          label: "Arrastar um bloco de reserva",
          explanation:
            "Troca a reserva de quarto ou de data direto na grade. Cuidado: a mudança é salva na hora, não existe uma confirmação depois — se arrastar sem querer, arraste de volta.",
        },
        {
          label: "Faixa hachurada vermelha",
          explanation:
            "Não é reserva: é manutenção. Aquele quarto está fora de operação naquele período e não aceita hóspede até a ordem ser concluída em Manutenções.",
          fact: (ctx) => {
            const fora = ctx.screen.rooms_out_of_order;
            if (fora === undefined) return null;
            return fora === 0
              ? "nenhum quarto fora de operação agora — a grade não deve ter faixa hachurada."
              : `${fora} quarto(s) fora de operação hoje.`;
          },
        },
        { label: 'Botão "Filtrar"', explanation: "Filtra a grade por tipo de quarto, status da reserva ou canal de origem, sem sair da tela." },
        { label: 'Botão "Bloquear Quarto"', explanation: "Abre o formulário pra registrar uma manutenção manual num quarto — motivo, data de início e previsão de liberação." },
        { label: 'Botão "Nova Reserva"', explanation: "Abre o formulário completo de criação de reserva (hóspede, quarto, datas, origem)." },
      ],
    },
  ],
  "/reservas": [
    {
      title: "Lista de reservas",
      description: "Busca por hóspede, código da reserva ou quarto. As abas de status filtram a lista, não mudam o status de nada.",
      items: [
        {
          label: "Status possíveis de uma reserva",
          explanation:
            "O caminho normal é Pendente → Confirmada → Hospedado (check-in) → Concluída (check-out). Fora dele existem Cancelada e Não compareceu. Uma reserva já finalizada (concluída, cancelada ou não compareceu) não volta atrás pelo fluxo normal.",
          // Lido do enum real do backend: a ajuda nunca cita um status que o
          // sistema não tem, nem esconde um que passou a existir.
          fact: (ctx) => {
            const rotulos: Record<string, string> = {
              PENDING: "Pendente",
              CONFIRMED: "Confirmada",
              CHECKED_IN: "Hospedado",
              CHECKED_OUT: "Concluída",
              CANCELLED: "Cancelada",
              NO_SHOW: "Não compareceu",
            };
            const lista = ctx.enums.booking_statuses.map((status) => rotulos[status] ?? status);
            return lista.length ? `o sistema trabalha com ${lista.join(", ")}.` : null;
          },
        },
        {
          label: "Quando o check-in libera",
          explanation:
            "O botão de check-in só funciona se a reserva estiver Confirmada e a data de chegada já tiver chegado — não dá pra dar entrada antes do dia. O check-out, por sua vez, só aparece para quem já está hospedado.",
          fact: (ctx) => {
            const { bookings_arriving_today: chegando, bookings_checked_in: hospedados } = ctx.screen;
            if (chegando === undefined && hospedados === undefined) return null;
            const partes: string[] = [];
            if (chegando !== undefined) {
              partes.push(
                chegando === 0
                  ? "nenhuma chegada confirmada para hoje"
                  : `${chegando} reserva(s) confirmada(s) chegando hoje, prontas para check-in`,
              );
            }
            if (hospedados !== undefined) {
              partes.push(hospedados === 0 ? "nenhum hóspede no hotel" : `${hospedados} hóspede(s) no hotel agora`);
            }
            return `${partes.join("; ")}.`;
          },
        },
      ],
    },
    {
      title: 'Modal "Nova Reserva"',
      items: [
        { label: "Nome do Hóspede", explanation: "Campo obrigatório." },
        { label: "E-mail", explanation: "Obrigatório — é usado pra enviar a confirmação e também pra reconhecer o hóspede: se já existir um cadastro com esse e-mail, a reserva usa o cadastro existente em vez de duplicar. O telefone não entra nessa busca." },
        { label: "Quarto", explanation: 'Opcional na criação: pode deixar "a definir" e alocar o quarto físico depois, no Mapa de Ocupação.' },
        { label: "Adultos / Crianças e bebês", explanation: "Usado para calcular a capacidade do quarto solicitado." },
        { label: "Origem", explanation: "De onde a reserva veio — direta, telefone, ou o nome do canal (Booking, Airbnb) se veio por integração automática." },
        { label: "Check-in / Check-out", explanation: "Datas de chegada e saída. A data de saída precisa ser depois da de chegada." },
      ],
    },
  ],
};
